import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { UserCheck, Plus, Loader2, Users, QrCode, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GuestsPage = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrGuest, setQrGuest] = useState(null);
  const [form, setForm] = useState({
    name: '',
    side: 'both',
    email: '',
    phone: '',
    rsvp_status: 'pending',
    guests_count: 1,
    dietary_restrictions: ''
  });

  useEffect(() => {
    fetchGuests();
  }, []);

  const fetchGuests = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/guests`, { withCredentials: true });
      setGuests(data);
    } catch (error) {
      toast.error('Error fetching guests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/guests`, {
        ...form,
        guests_count: parseInt(form.guests_count)
      }, { withCredentials: true });
      toast.success('Guest added!');
      setDialogOpen(false);
      setForm({ name: '', side: 'both', email: '', phone: '', rsvp_status: 'pending', guests_count: 1, dietary_restrictions: '' });
      fetchGuests();
    } catch (error) {
      toast.error('Error adding guest');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalGuestCount = guests.reduce((sum, guest) => sum + guest.guests_count, 0);
  const attendingCount = guests.filter(g => g.rsvp_status === 'attending').reduce((sum, g) => sum + g.guests_count, 0);
  const pendingCount = guests.filter(g => g.rsvp_status === 'pending').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Guests</h1>
          <p className="text-muted-foreground mt-1">Manage your wedding guest list</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-guest-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Guest
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Guest</DialogTitle>
              <DialogDescription>Add a guest to your wedding</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Guest Name</Label>
                <Input
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                  data-testid="guest-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Side</Label>
                  <Select value={form.side} onValueChange={(val) => setForm({...form, side: val})}>
                    <SelectTrigger data-testid="guest-side-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bride">Bride's Side</SelectItem>
                      <SelectItem value="groom">Groom's Side</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number of Guests</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.guests_count}
                    onChange={(e) => setForm({...form, guests_count: e.target.value})}
                    data-testid="guest-count-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="guest@email.com"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    data-testid="guest-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    data-testid="guest-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>RSVP Status</Label>
                <Select value={form.rsvp_status} onValueChange={(val) => setForm({...form, rsvp_status: val})}>
                  <SelectTrigger data-testid="guest-rsvp-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="attending">Attending</SelectItem>
                    <SelectItem value="not_attending">Not Attending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dietary Restrictions</Label>
                <Input
                  placeholder="e.g., Vegetarian, Vegan, Allergies"
                  value={form.dietary_restrictions}
                  onChange={(e) => setForm({...form, dietary_restrictions: e.target.value})}
                  data-testid="guest-dietary-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-guest-button">
                Add Guest
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Guests</p>
                <p className="text-2xl font-bold" data-testid="total-guests">{totalGuestCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Confirmed</p>
                <p className="text-2xl font-bold" data-testid="confirmed-guests">{attendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending RSVPs</p>
                <p className="text-2xl font-bold" data-testid="pending-rsvps">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {guests.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserCheck className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No guests yet. Add your first guest!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {guests.map((guest) => (
            <Card key={guest.id} className="shadow-sm" data-testid="guest-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-medium">{guest.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {guest.side === 'bride' ? "Bride's Side" : guest.side === 'groom' ? "Groom's Side" : 'Both'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        guest.rsvp_status === 'attending' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' :
                        guest.rsvp_status === 'not_attending' ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' :
                        'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {guest.rsvp_status === 'attending' ? 'Attending' : guest.rsvp_status === 'not_attending' ? 'Not Attending' : 'Pending'}
                      </span>
                      {guest.checked_in && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                          Checked In
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>Guests: {guest.guests_count}</span>
                      {guest.email && <span>{guest.email}</span>}
                      {guest.phone && <span>{guest.phone}</span>}
                      {guest.dietary_restrictions && <span>Diet: {guest.dietary_restrictions}</span>}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQrGuest(guest)}
                    data-testid="guest-qr-button"
                    className="rounded-full"
                  >
                    <QrCode className="h-4 w-4 mr-2" />
                    QR
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!qrGuest} onOpenChange={(open) => !open && setQrGuest(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-center">{qrGuest?.name}</DialogTitle>
            <DialogDescription className="text-center uppercase tracking-[0.2em] text-xs">
              Check-in QR Code
            </DialogDescription>
          </DialogHeader>
          {qrGuest && (
            <div className="flex flex-col items-center space-y-4 py-4" data-testid="qr-modal-content">
              <div className="p-6 bg-white rounded-lg border-2 border-border">
                <QRCodeCanvas
                  id="guest-qr-canvas"
                  value={`${window.location.origin}/checkin/${qrGuest.id}?token=${qrGuest.checkin_token || ''}`}
                  size={220}
                  level="H"
                  includeMargin={false}
                />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm text-muted-foreground">Party of {qrGuest.guests_count}</p>
                <p className="text-xs text-muted-foreground">Scan to check in on wedding day</p>
              </div>
              <div className="w-full space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Check-in Link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/checkin/${qrGuest.id}?token=${qrGuest.checkin_token || ''}`}
                    className="text-xs"
                    data-testid="checkin-link-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/checkin/${qrGuest.id}?token=${qrGuest.checkin_token || ''}`);
                      toast.success('Link copied!');
                    }}
                    data-testid="copy-link-button"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => {
                  const canvas = document.getElementById('guest-qr-canvas');
                  if (canvas) {
                    const url = canvas.toDataURL('image/png');
                    const link = document.createElement('a');
                    link.download = `${qrGuest.name.replace(/\s+/g, '_')}_qr.png`;
                    link.href = url;
                    link.click();
                  }
                }}
                data-testid="download-qr-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Download QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};